import axios from 'axios'
import hash from 'object-hash'

import { chartPalette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { datetimeXAxis } from '../utils/constants'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

type SectionType = 'members' | 'groups' | 'share'

interface HealthcareChartOptions {
  operadoras?: string[]
  operadora?: string[]
  segmento_plano: 'MEDICO' | 'ODONTOLOGICO'
  regiao?: string[]
  uf?: string[]
  municipio?: string[]
  rolling_value?: number
  regiao_grupo?: 'MUNICIPIO' | 'ESTADO' | 'REGIAO'
  share_minimo?: number
}

const HEALTHCARE_CHARTS_PATH = '/api/charts/healthcare'
const HEALTHCARE_TIMEOUT_MS = 120000

export namespace Healthcare {
  export namespace Categorical {
    const CONTEXT_ID = 'equity:healthcare:categorical'

    export const __typelist: {
      config: ChartConfig
      data: CategoricalChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      CategoricalChartData
    >

    type CategoryChartType =
      | 'faixa-etaria'
      | 'net-add-operadora'
      | 'growth-yoy-operadora'

    export interface ChartConfig {
      title?: string
      chartType: CategoryChartType
      section: SectionType
      chartOptions: HealthcareChartOptions
      context: typeof CONTEXT_ID
    }

    export interface CategoricalChartData extends CompassAPIDataBase {
      title: string
      data: {
        name: string
        value: number
      }[]
    }

    const convertCategoricalKeysToDefault = (data: any) =>
      data.values.map((item: any) => ({
        name: item.category,
        value: item.value,
      }))

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: `${HEALTHCARE_CHARTS_PATH}/${config.section}/${config.chartType}`,
        method: 'POST',
        data: config.chartOptions,
        timeout: HEALTHCARE_TIMEOUT_MS,
      }).then((response) => ({
        ...response.data,
        data: response.data.data
          ? convertCategoricalKeysToDefault(response.data.data[0])
          : response.data.result,
      }))

    const getChartProps = (config: ChartConfig, data: CategoricalChartData) => {
      const creditsConfig = getDataSources(data.data_sources)

      const getValueSuffix = (chart: CategoryChartType) =>
        ['growth-yoy-operadora', 'faixa-etaria'].includes(chart) ? '%' : ''

      const series = [
        {
          color: chartPalette[0],
          data: data.data.map((point) => [point.name, point.value * 100]),
          marker: { enabled: false },
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: getValueSuffix(config.chartType),
            }),
            valueDecimals: 2,
            valueSuffix: getValueSuffix(config.chartType),
          },
          type: 'column',
          yAxis: config.chartType !== 'faixa-etaria' ? 'variation' : 'share',
        },
      ]

      const yAxis = [
        {
          id: 'variation',
          opposite: false,
          title: { text: '' },
          startOnTick: false,
          endOnTick: false,
        },
        {
          id: 'share',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
          startOnTick: false,
          endOnTick: false,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            legend: { enabled: false },
            series,
            tooltip: {
              shared: false,
              split: true,
            },
            yAxis,
            xAxis: [{ type: 'category' }],
            chart: { inverted: config.chartType !== 'faixa-etaria' },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Series {
    const CONTEXT_ID = 'equity:healthcare:series'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type SeriesChartType =
      | 'evolucao-beneficiarios'
      | 'share'
      | 'evolucao-beneficiarios-yoy'
      | 'net-add'
      | 'adesao-cancelamento'
      | 'penetracao'
      | 'growth'
    export interface ChartConfig {
      title?: string
      chartType: SeriesChartType
      section: SectionType
      chartOptions: HealthcareChartOptions
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: `${HEALTHCARE_CHARTS_PATH}/${config.section}/${config.chartType}`,
        method: 'POST',
        data: config.chartOptions,
        timeout: HEALTHCARE_TIMEOUT_MS,
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const creditsConfig = getDataSources(data.data_sources)

      const getSeriesProps = (chartType: SeriesChartType) => {
        switch (chartType) {
          case 'share':
            return {
              type: 'area',
              yAxisName: 'share',
              tooltipValueSuffix: '%',
              valueFactor: 100,
            }
          case 'evolucao-beneficiarios-yoy':
          case 'penetracao':
            return {
              type: 'spline',
              yAxisName: 'percentage',
              tooltipValueSuffix: '%',
              valueFactor: 100,
            }
          default:
            return {
              type: 'spline',
              yAxisName: 'default',
              tooltipValueSuffix: '',
              valueFactor: 1,
            }
        }
      }

      const {
        type,
        yAxisName,
        tooltipValueSuffix,
        valueFactor,
      } = getSeriesProps(config.chartType)

      const series = data.data?.map((s, idx) => ({
        color: chartPalette[idx],
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          value * valueFactor,
        ]),
        id: s.name,
        marker: { enabled: false },
        name: s.name.replaceAll('_', ' '),
        tooltip: {
          pointFormatter: magnitudePointFormatter({ unit: tooltipValueSuffix }),
          valueDecimals: 2,
          valueSuffix: tooltipValueSuffix,
          xDateFormat: config.chartType === 'penetracao' ? ' %Y' : '%B %Y',
        },
        type,
        yAxis: yAxisName,
      }))

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
        },
        {
          id: 'share',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
          max: 100,
        },
        {
          id: 'percentage',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            series,
            tooltip: {
              shared: config.chartType === 'adesao-cancelamento',
              split: config.chartType !== 'adesao-cancelamento',
            },
            xAxis: datetimeXAxis,
            yAxis,
            plotOptions: { area: { stacking: 'normal' } },
            legend: {
              enabled: config.chartType !== 'penetracao',
              maxHeight: 60,
            },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace SliderSeries {
    const CONTEXT_ID = 'equity:healthcare:slider_series'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      chartOptions: HealthcareChartOptions
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: `${HEALTHCARE_CHARTS_PATH}/share/growth`,
        method: 'POST',
        data: config.chartOptions,
        timeout: HEALTHCARE_TIMEOUT_MS,
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          useRegularChart: true,
          stockTools: false,
          xAxis: datetimeXAxis[0],
          tooltip: { xDateFormat: '%B %Y' },
          yAxis: [{ title: { text: '' }, labelSuffix: '%' }],
        },
        {
          name: data.data.map((s) => s.name.replaceAll('_', ' ')),
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }
  export namespace Valuation {
    const CONTEXT_ID = 'equity:healthcare:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'pe_rdor3'
        | 'pe_hapv3'
        | 'pe_gndi3'
        | 'pe_sula11'
        | 'pe_qual3'
        | 'pe_odpv3'
        | 'pe_pard3'
        | 'pe_flry3'
        | 'pe_dasa3'
        | 'pe_blau3'
        | 'pe_matd3'
        | 'pe_krsa3'
        | 'pe_aalr3'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getValuationChartProps(config, data),
    }

    const getValuationChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)
  }
}
